import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@material-ui/core";
import { toast } from "react-toastify";
import CloseIcon from "../../assets/icon/CloseIcon";
import SearchIcon from "../../assets/icon/SearchIcon";
import DownArrow from "../../assets/icon/DownArrow";
import CancelIcon from '@material-ui/icons/Cancel'; // Importa el icono de cancelar
import { TicketsContext } from "../../context/Tickets/TicketsContextIndexedDB";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiDialog-paper": {
      backgroundColor: theme.palette.tabHeaderBackground,
      color: theme.palette.text.primary,
      borderRadius: 20
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeButton: {
    color: theme.palette.text.primary,
  },
  content: {
    paddingTop: theme.spacing(2),
  },
  input: {
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.23)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255, 255, 255, 0.87)",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-outlined": {
      color: theme.palette.text.secondary,
    },
  },
  select: {
    "&.MuiOutlinedInput-root": {
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.23)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255, 255, 255, 0.87)",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-outlined": {
      color: theme.palette.text.secondary,
    },
  },
  chip: {
    margin: theme.spacing(0.5),
    '& .MuiChip-deleteIcon': {
      color: 'inherit',
      '&:hover': {
        color: theme.palette.grey[100],
      },
    },
  },
  icon: {
    right: theme.spacing(1),
    position: "absolute",
    pointerEvents: "none",
  },
  menuItem: {
    padding: theme.spacing(1, 2),
    color: theme.palette.text.primary,
  },
  btnWrapper: {
    position: "relative",
    borderRadius: 10,
    padding: 10,
    width: 100,
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: '0.75rem',
  },
}));

const filter = createFilterOptions({
  trim: true,
});

const NewTicketModal = ({ modalOpen, onClose, initialContact }) => {
  const classes = useStyles();
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [connections, setConnections] = useState([]);
  const [allConnections, setAllConnections] = useState([]);
  const [availableQueues, setAvailableQueues] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({});
  const { user } = useContext(AuthContext);
  const { handleNewTicket } = useContext(TicketsContext);

  const theme = useTheme();

  useEffect(() => {
    if (!modalOpen) {
      setSearchParam("");
      setSelectedContact(null);
      setSelectedQueue([]);
      setSelectedConnection(null);
      setConnections([]);
      setAllConnections([]);
      setAvailableQueues([]);
      setOptions([]);
      setNewContact({});
    } else if (initialContact?.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }

    // Resetear departamentos disponibles al abrir el modal
    if (modalOpen) {
      setAvailableQueues([]);
    }

    // Cargar las conexiones activas al abrir el modal
    const loadConnections = async () => {
      try {
        const { data } = await api.get(`/whatsapp?session=0&companyId=${user.companyId}`);
        setAllConnections(data);
        setConnections(data);
        
        // Si solo hay una conexión, seleccionarla automáticamente
        if (data.length === 1) {
          setSelectedConnection(data[0].id);
        }
      } catch (err) {
        toastError(err);
      }
    };

    if (modalOpen) {
      loadConnections();
    }
  }, [modalOpen, initialContact, user.queues]);

  // Filtrar departamentos basándose en la conexión seleccionada
  useEffect(() => {
    const filterQueues = async () => {
      if (selectedConnection && allConnections.length > 0) {
        try {
          // Obtener la conexión seleccionada con sus departamentos
          const { data: connectionData } = await api.get(`/whatsapp/${selectedConnection}`);
          
          if (connectionData && connectionData.whatsappQueues) {
            let filteredQueues = connectionData.whatsappQueues;
            
            const userQueueIds = user.queues;
            filteredQueues = connectionData.whatsappQueues.filter(queue => 
              userQueueIds.includes(queue.queueId)
            );
            
            setAvailableQueues(filteredQueues.map(q => q.queue));
            
            // Auto-seleccionar si solo hay un departamento disponible
            if (filteredQueues.length === 1) {
              setSelectedQueue([filteredQueues[0].queue.id]);
            } else {
              // Resetear selección si había un departamento seleccionado que ya no está disponible
              if (selectedQueue.length > 0) {
                const isCurrentQueueAvailable = filteredQueues.some(q => q.queue.id === selectedQueue[0]);
                if (!isCurrentQueueAvailable) {
                  setSelectedQueue([]);
                }
              }
            }
          } else {
            setAvailableQueues([]);
            setSelectedQueue([]);
          }
        } catch (err) {
          console.error("Error al obtener departamentos de la conexión:", err);
          setAvailableQueues([]);
          setSelectedQueue([]);
        }
      } else {
        // Si no hay conexión seleccionada, resetear departamentos
        setAvailableQueues([]);
        setSelectedQueue([]);
      }
    };

    filterQueues();
  }, [selectedConnection, allConnections, user.profile, user.queues]);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleClose = () => {
    setSearchParam("");
    setSelectedContact(null);
    setSelectedQueue([]);
    setSelectedConnection(null);
    setConnections([]);
    setAllConnections([]);
    setAvailableQueues([]);
    setOptions([]);
    setNewContact({});
    onClose();
  };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    if (!selectedConnection) {
      toast.error("Seleccione una conexión");
      return;
    }
    setLoading(true);
    try {
      const queueId = selectedQueue.length > 0 ? selectedQueue[0] : null;
      
      // Obtener la cola seleccionada y su conexión
      const selectedQueueData = availableQueues.find(q => q.id === queueId);
      const connectionTag = selectedQueueData?.connections;
      
      const { data: { ticket, alreadyExists } } = await api.post("/tickets", {
        contactId: contactId,
        queueId,
        userId: user.id,
        status: "open",
        whatsappId: selectedConnection
      });

      if (alreadyExists && ticket.user.id !== user.id) {
        toast.error(`Este contacto ya está siendo atendido por el usuario ${ticket.user?.name}`);
        setLoading(false);
        return;
      }

      // Si hay una conexión, actualizar las etiquetas del ticket
      if (connectionTag) {
        await api.post(`/tags/sync`, {
          ticketId: ticket.id,
          tags: [connectionTag]
        });
      }

      handleNewTicket(ticket);
      onClose();
      
      // Navegar automáticamente al nuevo ticket creado
      if (ticket.uuid) {
        console.log(`🚀 Navegando al nuevo ticket creado: ${ticket.uuid}`);
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSelectOption = (e, newValue) => {
    if (newValue?.number) {
      setSelectedContact(newValue);
    } else if (newValue?.name) {
      // Validar si el nombre contiene solo números
      const isOnlyNumbers = /^\d+$/.test(newValue.name.trim());
      
      if (isOnlyNumbers) {
        // Si son solo números, colocarlos en el campo número con el prefijo +
        setNewContact({ 
          name: "",
          number: "+" + newValue.name.trim(),
          email: "" 
        });
      } else {
        // Si no son solo números, colocar en el campo nombre
        setNewContact({ 
          name: newValue.name,
          number: "+",
          email: "" 
        });
      }
      setContactModalOpen(true);
    }
  };

  const handleAddNewContactTicket = async (contact) => {
    try {
      const { data } = await api.get("contacts", {
        params: { searchParam: contact.number }
      });
      
      if (data.contacts && data.contacts.length > 0) {
        const newContact = data.contacts[0];
        setSelectedContact(newContact);
        setOptions(data.contacts);
      }
      setContactModalOpen(false);
    } catch (err) {
      toastError(err);
      setContactModalOpen(false);
    }
  };

  const handleCloseContactModal = (success, contact) => {
    if (success && contact) {
      handleAddNewContactTicket(contact);
    } else {
      setContactModalOpen(false);
    }
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);

    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }

    return filtered;
  };

  const renderOption = (option) => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${i18n.t("newTicketModal.add")} ${option.name}`;
    }
  };

  const renderOptionLabel = (option) => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };

  const handleRemoveQueue = (event, queueId) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedQueue(prevQueues => prevQueues.filter(id => id !== queueId));
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'whatsapp':
        return '📱 ';
      case 'facebook':
        return '📘 ';
      case 'instagram':
        return '📷 ';
      default:
        return '';
    }
  };

  return (
    <>
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        initialValues={newContact}
      />
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        className={classes.root}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">{i18n.t("newTicketModal.title")}</Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <Grid container spacing={2}>
            {(initialContact === undefined ||
              initialContact.id === undefined) && (
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  options={options}
                  loading={loading}
                  value={selectedContact}
                  clearOnBlur
                  autoHighlight
                  clearOnEscape
                  getOptionLabel={renderOptionLabel}
                  renderOption={renderOption}
                  filterOptions={createAddContactOption}
                  onChange={(e, newValue) => handleSelectOption(e, newValue)}
                  renderInput={(params) => (
                    <>
                      <TextField
                        {...params}
                        label={i18n.t("newTicketModal.fieldLabel")}
                        variant="outlined"
                        autoFocus
                        onChange={(e) => setSearchParam(e.target.value)}
                        className={classes.input}
                        onKeyPress={(e) => {
                          if (loading || !selectedContact) return;
                          else if (e.key === "Enter") {
                            handleSaveTicket(selectedContact.id);
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                          startAdornment: (
                            <div style={{ marginTop: 5, marginLeft: 5 }}>
                              <SearchIcon
                                fill={theme.palette.messageIcons}
                                height={20}
                                width={20}
                              />
                            </div>
                          ),
                        }}
                      />
                      {!selectedContact && searchParam && searchParam.length >= 3 && !loading && (
                        <Typography className={classes.errorText}>
                          Por favor, seleccione un contacto válido
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Seleccione una conexión</InputLabel>
                <Select
                  label="Seleccione una conexión"
                  fullWidth
                  value={selectedConnection || ''}
                  onChange={(e) => setSelectedConnection(e.target.value)}
                  className={classes.select}
                  IconComponent={() => (
                    <div className={classes.icon}>
                      <DownArrow fill={theme.palette.messageIcons} />
                    </div>
                  )}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                >
                  {connections?.length > 0 ? (
                    connections.filter((conn) => conn.channel !== 'webchat').map((conn) => (
                      <MenuItem
                        key={conn.id}
                        value={conn.id}
                        className={classes.menuItem}
                      >
                        <ListItemText 
                          primary={`${getChannelIcon(conn.channel)}${conn.name}`} 
                        />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled className={classes.menuItem}>
                      <ListItemText primary="No hay conexiones disponibles" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Seleccione un departamento</InputLabel>
                <Select
                  label="Seleccione un departamento"
                  fullWidth
                  value={selectedQueue}
                  onChange={(e) => setSelectedQueue([e.target.value])}
                  className={classes.select}
                  renderValue={(selected) => (
                    <div>
                      {selected.map((value) => {
                        const queue = availableQueues.find((q) => q.id === value);
                        return (
                          <Chip
                            key={queue.id}
                            label={queue.name}
                            onDelete={(event) => handleRemoveQueue(event, queue.id)}
                            className={classes.chip}
                            style={{ backgroundColor: queue.color }}
                            deleteIcon={
                              <CancelIcon 
                                style={{ color: 'inherit' }}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                }}
                              />
                            }
                          />
                        );
                      })}
                    </div>
                  )}
                  IconComponent={() => (
                    <div className={classes.icon}>
                      <DownArrow fill={theme.palette.messageIcons} />
                    </div>
                  )}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                >
                  {availableQueues?.length > 0 ? (
                    availableQueues.map((queue) => (
                      <MenuItem
                        key={queue.id}
                        value={queue.id}
                        className={classes.menuItem}
                      >
                        <ListItemText primary={queue.name} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled className={classes.menuItem}>
                      <ListItemText primary={
                        selectedConnection 
                          ? "Sin departamentos disponibles para esta conexión" 
                          : "Seleccione primero una conexión"
                      } />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
            disabled={loading}
            variant="outlined"
            className={classes.btnWrapper}
          >
            {i18n.t("newTicketModal.buttons.cancel")}
          </Button>
          <ButtonWithSpinner
            variant="containedPrimary"
            type="button"
            disabled={!selectedContact || !selectedConnection}
            onClick={() => handleSaveTicket(selectedContact.id)}
            loading={loading}
            className={classes.btnWrapper}
            style={{ color: "black" }}
          >
            {i18n.t("newTicketModal.buttons.ok")}
          </ButtonWithSpinner>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewTicketModal;
